import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Language from '../../../types/language/Language';
import LanguageWriteDTO from '../../../types/language/LanguageWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class LanguageService {

    private RESOURCE: string = 'language';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the languages from the backend.
     *
     * @param search The search query to filter the languages by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The languages.
     */
    public async getLanguages(search?: string, page: number = 0, size: number = 10): Promise<Page<Language>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<Language>>(this.RESOURCE, params);
    }

    /**
     * Fetches multiple languages from the backend.
     *
     * @param ids The IDs of the languages to fetch.
     * @return The languages.
     */
    public async getMultipleLanguages(ids: number[]): Promise<Language[]> {
        const params = new HttpParams().set('ids', ids.join(','));

        return await this.baseService.get<Language[]>(this.RESOURCE + '/multiple', params);
    }

    /**
     * Fetches all languages from the backend.
     *
     * @return The languages.
     */
    public async getAllLanguages(): Promise<Language[]> {
        return await this.baseService.get<Language[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a language from the backend.
     *
     * @param id The ID of the language to fetch.
     * @return The language.
     */
    public async getLanguage(id: number): Promise<Language> {
        return await this.baseService.get<Language>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a language in the backend.
     *
     * @param language The language to create.
     * @return The response from the backend.
     */
    public async createLanguage(language: LanguageWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, language);
    }

    /**
     * Updates a language in the backend.
     *
     * @param language The language to update.
     * @return The response from the backend.
     */
    public async updateLanguage(language: LanguageWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + language.id, language);
    }

    /**
     * Deletes a language in the backend.
     *
     * @param id The ID of the language to delete.
     * @return The response from the backend.
     */
    public async deleteLanguage(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
